import { ref, onMounted, onUnmounted } from 'vue';
import VersionService from '@/services/VersionService';
import { VERSION_CONFIG, getEnvironmentConfig, getBuildInfo } from '@/config/version';
import {
  setStaleFlag,
  setHandleStaleness,
  setCheckVersionFn,
} from '@/utils/versionState';
import * as Sentry from '@sentry/vue';

export function useVersionCheck() {
  const config = getEnvironmentConfig();
  const buildInfo = getBuildInfo();

  const showUpdateModal = ref(false);
  const versionInfo = ref({
    current_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    current_commit: typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'unknown',
    server_version: null,
    server_commit: null,
    isStale: false,
    check_interval: config.CHECK_INTERVAL,
    last_check: null,
    build_info: buildInfo,
  });

  const isChecking = ref(false);
  const checkInterval = ref(null);
  const hiddenTimestamp = ref(null);
  const isInReloadLoop = ref(false);

  /**
   * Obtiene las timestamps de reloads recientes desde sessionStorage
   * @returns {number[]}
   */
  const getReloadTimestamps = () => {
    try {
      const stored = sessionStorage.getItem(VERSION_CONFIG.STORAGE_KEYS.RELOAD_TIMESTAMPS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  /**
   * Guarda las timestamps de reloads en sessionStorage
   * @param {number[]} timestamps
   */
  const setReloadTimestamps = (timestamps) => {
    try {
      sessionStorage.setItem(
        VERSION_CONFIG.STORAGE_KEYS.RELOAD_TIMESTAMPS,
        JSON.stringify(timestamps),
      );
    } catch {
      // sessionStorage no disponible
    }
  };

  /**
   * Limpia las timestamps de reloads antiguos
   */
  const cleanOldReloadTimestamps = () => {
    const now = Date.now();
    const windowMs = VERSION_CONFIG.RELOAD_LOOP_WINDOW_MS;
    const timestamps = getReloadTimestamps().filter((ts) => now - ts < windowMs);
    setReloadTimestamps(timestamps);
    return timestamps;
  };

  /**
   * Verifica si podemos hacer auto-reload sin entrar en loop
   * @returns {boolean}
   */
  const canAutoReload = () => {
    const timestamps = cleanOldReloadTimestamps();
    return timestamps.length < VERSION_CONFIG.MAX_AUTO_RELOADS;
  };

  /**
   * Registra un reload y ejecuta la recarga
   */
  const performReload = () => {
    const timestamps = cleanOldReloadTimestamps();
    timestamps.push(Date.now());
    setReloadTimestamps(timestamps);

    Sentry.addBreadcrumb({
      message: 'Auto-reload triggered due to stale version',
      category: 'version',
      level: 'info',
      data: {
        reloadCount: timestamps.length,
        currentCommit: versionInfo.value.current_commit,
        serverCommit: versionInfo.value.server_commit,
      },
    });

    // Limpiar service worker si existe
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }

    // Reload con cache busting, preservando el hash route
    const timestamp = Date.now();
    const hash = window.location.hash || '';
    window.location.href = `${window.location.origin}${window.location.pathname}?v=${timestamp}${hash}`;
  };

  /**
   * Resetea el contador de reloads cuando la versión está actualizada
   */
  const resetReloadCounter = () => {
    setReloadTimestamps([]);
    isInReloadLoop.value = false;
  };

  /**
   * Maneja la detección de versión desactualizada
   */
  const handleStaleness = () => {
    if (canAutoReload()) {
      performReload();
    } else {
      // Estamos en un loop de reloads, mostrar modal persistente
      isInReloadLoop.value = true;
      showUpdateModal.value = true;

      Sentry.captureMessage('Reload loop detected - showing persistent modal', {
        level: 'warning',
        tags: {
          version_check: 'reload_loop',
          current_commit: versionInfo.value.current_commit,
        },
        extra: {
          reloadTimestamps: getReloadTimestamps(),
        },
      });
    }
  };

  /**
   * Verifica la versión de la aplicación
   */
  const checkVersion = async () => {
    if (isChecking.value) return;

    isChecking.value = true;

    try {
      const result = await VersionService.checkVersion();

      // Actualizar información de versión
      if (result.serverData) {
        versionInfo.value = {
          ...versionInfo.value,
          server_version: result.serverData.version,
          server_commit: result.serverData.gitCommit,
          isStale: result.isStale,
          last_check: new Date().toISOString(),
        };
      }

      // Actualizar flag global
      setStaleFlag(result.isStale);

      if (result.isStale) {
        Sentry.addBreadcrumb({
          message: 'Stale version detected',
          category: 'version',
          level: 'warning',
          data: {
            currentCommit: versionInfo.value.current_commit,
            serverCommit: versionInfo.value.server_commit,
            reason: result.reason,
          },
        });

        handleStaleness();
      } else {
        // Versión actualizada, resetear contador de reloads
        resetReloadCounter();

        Sentry.addBreadcrumb({
          message: 'Version check completed - up to date',
          category: 'version',
          level: 'info',
          data: {
            currentCommit: versionInfo.value.current_commit,
            reason: result.reason,
          },
        });
      }
    } catch (error) {
      console.error('Error checking version:', error);

      Sentry.captureException(error, {
        tags: {
          service: 'useVersionCheck',
          method: 'checkVersion',
        },
      });
    } finally {
      isChecking.value = false;
    }
  };

  /**
   * Configura verificación periódica de versión
   */
  const setupPeriodicCheck = () => {
    if (checkInterval.value) {
      clearInterval(checkInterval.value);
    }

    checkInterval.value = setInterval(() => {
      checkVersion();
    }, config.CHECK_INTERVAL);
  };

  /**
   * Para la verificación periódica
   */
  const stopPeriodicCheck = () => {
    if (checkInterval.value) {
      clearInterval(checkInterval.value);
      checkInterval.value = null;
    }
  };

  /**
   * Maneja errores de carga de chunks (dynamic imports)
   * @param {Error} error
   * @returns {boolean} true si fue manejado como chunk error
   */
  const handleChunkError = (error) => {
    const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module')
      || error?.message?.includes('Loading chunk')
      || error?.message?.includes('Loading CSS chunk')
      || error?.name === 'ChunkLoadError';

    if (isChunkError) {
      Sentry.addBreadcrumb({
        message: 'Chunk load error detected - triggering reload',
        category: 'version',
        level: 'error',
        data: {
          errorMessage: error?.message,
          currentCommit: versionInfo.value.current_commit,
        },
      });

      // Marcar como stale y manejar
      setStaleFlag(true);
      handleStaleness();
      return true;
    }

    return false;
  };

  /**
   * Handler para visibility change (tab oculto/visible)
   */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      hiddenTimestamp.value = Date.now();
    } else if (hiddenTimestamp.value) {
      const hiddenDuration = Date.now() - hiddenTimestamp.value;
      const minHiddenDuration = config.MIN_HIDDEN_DURATION_MS || 5 * 60 * 1000;

      if (hiddenDuration >= minHiddenDuration) {
        Sentry.addBreadcrumb({
          message: 'Tab returned from hidden state - checking version',
          category: 'version',
          level: 'info',
          data: {
            hiddenDurationMs: hiddenDuration,
          },
        });

        checkVersion();
      }

      hiddenTimestamp.value = null;
    }
  };

  /**
   * Handler para window focus
   */
  const handleWindowFocus = () => {
    if (hiddenTimestamp.value) {
      const hiddenDuration = Date.now() - hiddenTimestamp.value;
      const minHiddenDuration = config.MIN_HIDDEN_DURATION_MS || 5 * 60 * 1000;

      if (hiddenDuration >= minHiddenDuration) {
        checkVersion();
      }
    }
  };

  /**
   * Handler global para errores de window (chunk errors)
   */
  const handleGlobalError = (event) => {
    if (event.error) {
      handleChunkError(event.error);
    }
  };

  /**
   * Handler para unhandled rejections (chunk errors en promises)
   */
  const handleUnhandledRejection = (event) => {
    if (event.reason) {
      handleChunkError(event.reason);
    }
  };

  /**
   * Maneja la actualización manual desde el modal
   */
  const handleUpdate = () => {
    Sentry.addBreadcrumb({
      message: 'Manual update initiated from modal',
      category: 'user_action',
      level: 'info',
    });

    performReload();
  };

  /**
   * Maneja el dismiss del modal (solo si no es loop)
   */
  const handleDismiss = () => {
    if (!isInReloadLoop.value) {
      showUpdateModal.value = false;

      Sentry.addBreadcrumb({
        message: 'User dismissed version update modal',
        category: 'user_action',
        level: 'info',
      });
    }
    // Si estamos en loop, no permitimos dismiss
  };

  // Inicializar al montar
  onMounted(() => {
    // Registrar funciones en el módulo de estado global
    setHandleStaleness(handleStaleness);
    setCheckVersionFn(checkVersion);

    // Primera verificación después de 3 segundos
    setTimeout(checkVersion, 3000);

    // Configurar verificación periódica
    setupPeriodicCheck();

    // Listeners de visibilidad y focus
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    // Listeners globales para chunk errors
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
  });

  // Limpiar al desmontar
  onUnmounted(() => {
    stopPeriodicCheck();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleWindowFocus);
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  });

  return {
    showUpdateModal,
    versionInfo,
    isChecking,
    isInReloadLoop,
    checkVersion,
    handleUpdate,
    handleDismiss,
    handleChunkError,
    setupPeriodicCheck,
    stopPeriodicCheck,
  };
}
